import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { type IRetrieveWorkspaceQuery } from '@telespot/management/use-cases';
import { SearchableSingleSelectorItem } from '@shared/ui';
import { WorkspacesEffects } from '../../../stores/workspaces.effects';
import { RETRIEVE_WORKSPACE_QUERY } from '../../../stores/workspace-detail.effects';

export interface CompatibleWSDialogData {
  workspaceId: string;
  acceptMessage: string;
}

@Injectable()
export class CompatibleWSDialogFacade extends ComponentStore<Record<string, never>> {
  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: CompatibleWSDialogData,
    private readonly workspacesEffects: WorkspacesEffects,
    @Inject(RETRIEVE_WORKSPACE_QUERY) private readonly retrieveWorkspaceQuery: IRetrieveWorkspaceQuery
  ) {
    super({});
    this.initCompatibleWorkspaces();
  }

  // Selectors
  public get workspaceOptions$() {
    return this.workspacesEffects.workspaces$.pipe(
      map((workspaces) =>
        workspaces
          .filter((ws) => ws.id !== this.data.workspaceId)
          .map((ws): SearchableSingleSelectorItem => ({ id: ws.id, name: ws.name }))
      )
    );
  }

  public get loading$() {
    return this.workspacesEffects.loading$;
  }

  public get loaded$() {
    return this.workspacesEffects.hasMoreItems$.pipe(map((hasMoreItems) => !hasMoreItems));
  }

  public get noWorkspaces$() {
    return this.workspacesEffects.noItems$;
  }

  // Delegated effects / updaters
  public get loadMoreWorkspaces() {
    return this.workspacesEffects.loadMoreItems;
  }

  public get setSearchTerm() {
    return this.workspacesEffects.setSearchTerm;
  }

  // Effects
  private readonly initCompatibleWorkspaces = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        from(this.retrieveWorkspaceQuery.execute(this.data.workspaceId)).pipe(
          tap((workspace) => {
            if (!workspace) return;
            this.workspacesEffects.setCaseTypeFilter(workspace.caseTypeId);
            this.workspacesEffects.setOrganizationId(workspace.organizationId);
          }),
          catchError(() => of(undefined))
        )
      )
    )
  );
}
